import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { hardDeleteDocument, restoreDocument } from '../../../lib/services/documentServices';
import Icon from '../../../components/AppIcon';

import DashboardSidebar from '../../../components/navigation/DashboardSidebar';
import DashboardHeader from '../../../components/navigation/DashboardHeader';
import DashboardFooter from '../../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../../components/navigation/MobileMenuButton';

const CestinoAziendale = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [actionId, setActionId] = useState(null);

  const [autoDelete, setAutoDelete] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [idAzienda, setIdAzienda] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); 

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); 
  const [selectedDoc, setSelectedDoc] = useState(null);

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2) + ' MB';
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '---';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchCestino = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: adminProfil } = await supabase
        .from('profili')
        .select('id_azienda, ruolo')
        .eq('id', user.id)
        .single();

      if (adminProfil) {
        setUserRole(adminProfil.ruolo);
        setIdAzienda(adminProfil.id_azienda);

        const { data: aziendaData } = await supabase
          .from('aziende')
          .select('auto_delete')
          .eq('id', adminProfil.id_azienda)
          .single();
        
        if (aziendaData) setAutoDelete(aziendaData.auto_delete);

        const { data: docs, error: docsError } = await supabase
          .from('documenti')
          .select(`
            *,
            profili:id_utente(nome, cognome, email)
          `)
          .eq('id_azienda', adminProfil.id_azienda)
          .eq('is_deleted', true)
          .order('deleted_at', { ascending: false });

        if (docsError) throw docsError;

        const adminIds = [...new Set(docs?.map(d => d.deleted_by).filter(Boolean))];
        
        if (adminIds.length > 0) {
          const { data: admins } = await supabase
            .from('profili')
            .select('id, nome, cognome, email')
            .in('id', adminIds);

          const docsWithAdmins = docs.map(doc => ({
            ...doc,
            admin_data: admins?.find(a => a.id === doc.deleted_by) || null
          }));
          setDeletedDocs(docsWithAdmins);
        } else {
          setDeletedDocs(docs || []);
        }
      }
    } catch (err) {
      console.error("Cestino Fetch Error:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCestino();
  }, [fetchCestino]);

  const handleToggleAutoDelete = async () => {
    const isAdmin = ['admin', 'superadmin', 'owner'].includes(userRole?.toLowerCase());
    if (!isAdmin || !idAzienda) return;

    const newValue = !autoDelete;
    setAutoDelete(newValue);

    try {
      const { error } = await supabase
        .from('aziende')
        .update({ auto_delete: newValue })
        .eq('id', idAzienda);
      if (error) throw error;
    } catch (err) {
      setAutoDelete(!newValue);
    }
  };

  const filteredDocs = useMemo(() => {
    return deletedDocs.filter(doc => {
      const matchesSearch = 
        doc.nome_file?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.profili?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.profili?.cognome?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;
      if (dateFilter === 'all') return true;

      const deleteDate = new Date(doc.deleted_at);
      const now = new Date();
      const diffTime = Math.abs(now - deleteDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (dateFilter === 'today') return deleteDate.toDateString() === now.toDateString();
      if (dateFilter === '7days') return diffDays <= 7;
      return true;
    });
  }, [deletedDocs, searchTerm, dateFilter]);

  const logAuditAction = async (doc, actionType) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profil } = await supabase.from('profili').select('nome, cognome, id_azienda, email, ruolo').eq('id', user.id).single();
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const localISOTime = (new Date(Date.now() - tzOffset)).toISOString().slice(0, -1);

      await supabase.from('audit_logs').insert([{
        id_azienda: profil?.id_azienda,
        executor_id: user.id,
        executor_uuid_text: user.id,
        executor_name: `${profil?.nome || ''} ${profil?.cognome || ''}`.trim(),
        executor_email: profil?.email || user.email,
        executor_role: profil?.ruolo || 'admin',
        target_user_id: doc.id_utente,
        target_uuid_text: doc.id_utente,
        action: actionType,
        file_name: doc.nome_file,
        file_id: doc.id,
        user_agent: navigator.userAgent,
        ip_address: 'Admin Trash Panel',
        created_at: localISOTime
      }]);
    } catch (err) { console.error(err); }
  };

  const openModal = (type, doc) => {
    setModalType(type);
    setSelectedDoc(doc);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDoc(null);
    setModalType(null);
  };

  const executeAction = async () => {
    if (!selectedDoc) return;
    const doc = selectedDoc;
    try {
      setActionId(doc.id);
      if (modalType === 'restore') {
        await restoreDocument(doc.id);
        await logAuditAction(doc, 'RESTORE');
      } else {
        await hardDeleteDocument(doc.id, doc.url_path);
        await logAuditAction(doc, 'HARD DELETE');
      }
      setDeletedDocs(prev => prev.filter(d => d.id !== doc.id));
      closeModal();
    } catch (err) { alert("Errore"); } finally { setActionId(null); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader userName="Admin Panel" />

      <main className="main-content with-sidebar py-10 px-4 mt-16 animate-in fade-in duration-300">
        <div className="max-w-7xl mx-auto">
          
          <div className="flex justify-start mb-6">
            <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-all">
              <div className="p-2 rounded-xl bg-muted/30 group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
              </div>
              <span className="text-[11px] font-black uppercase tracking-[0.2em]">{t('userLog.back')}</span>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div className="relative">
              <div className="flex flex-col relative z-10 bg-background pr-4 w-fit">
                <h2 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-3 text-red-600">
                  <div className="p-2 bg-red-600 rounded-lg text-white shadow-lg shadow-red-600/20">
                    <Icon name="Trash2" size={20} />
                  </div>
                  {t('trash.title')}
                </h2>
                <p className="text-xs text-muted-foreground font-semibold mt-1 italic opacity-80 tracking-wide ml-12">
                   {t('trash.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 bg-card p-2 rounded-[2rem] border border-border/50 shadow-sm pr-4">
              {['admin', 'superadmin', 'owner'].includes(userRole?.toLowerCase()) && (
                <div 
                  onClick={handleToggleAutoDelete}
                  className={`flex items-center gap-4 px-4 py-2 rounded-[1.5rem] cursor-pointer transition-all duration-300 border shadow-sm ${
                    autoDelete ? 'bg-red-600 border-red-700 hover:bg-red-700 shadow-red-500/20' : 'bg-muted/30 border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-black uppercase tracking-tighter leading-none transition-colors ${autoDelete ? 'text-white' : 'text-foreground'}`}>
                      {t('settings_trash.delete_label')}
                    </span>
                    <span className={`text-[9px] font-bold uppercase opacity-70 transition-colors ${autoDelete ? 'text-red-100' : 'text-muted-foreground'}`}>
                      {t('settings_trash.days_30')}
                    </span>
                  </div>
                  <div className={`w-10 h-5 rounded-full p-1 transition-colors duration-300 relative ${autoDelete ? 'bg-red-900/40' : 'bg-muted-foreground/30'}`}>
                    <div className={`w-3 h-3 rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${autoDelete ? 'translate-x-5 bg-white' : 'translate-x-0 bg-muted-foreground'}`} />
                  </div>
                </div>
              )}
              <div className="h-8 w-[1px] bg-border/50 hidden sm:block" />
              <div className="relative w-full sm:w-64">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-muted-foreground">
                  <Icon name="Search" size={14} />
                </div>
                <input 
                  type="text"
                  placeholder={t('trash.placeholder_search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-muted/30 border-none rounded-xl py-2 pl-9 pr-4 text-xs font-bold focus:ring-2 focus:ring-red-600/20 transition-all outline-none"
                />
              </div>
              <div className="flex gap-1 bg-muted/20 p-1 rounded-xl">
                {[
                  { id: 'all', label: t('trash.filter_all') },
                  { id: 'today', label: t('trash.filter_today') },
                  { id: '7days', label: t('trash.filter_7days') }
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setDateFilter(f.id)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${dateFilter === f.id ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-card rounded-[2.5rem] border border-border/60 shadow-sm overflow-hidden flex flex-col">
            <div className="overflow-y-auto max-h-[600px] custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-20 bg-muted/90 backdrop-blur-md">
                  <tr className="border-b border-border/50">
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('trash.table_doc')}</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('trash.table_size')}</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('trash.table_owner')}</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground text-center">{t('trash.table_removed_by')}</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('trash.table_date')}</th>
                    <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground text-right">{t('trash.table_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {loading ? (
                    <tr><td colSpan="6" className="p-20 text-center"><Icon name="Loader2" className="animate-spin mx-auto text-primary" size={32} /></td></tr>
                  ) : filteredDocs.length > 0 ? (
                    filteredDocs.map((doc) => (
                      <tr key={doc.id} className="hover:bg-red-500/[0.01] transition-colors group">
                        <td className="px-6 py-5">
                          <div className="flex flex-col text-sm font-bold text-foreground">
                            <span className="truncate max-w-[180px] font-black">{doc.nome_file}</span>
                            <span className="text-[10px] font-black text-red-500 uppercase tracking-tighter">{doc.tipologia}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-xs font-black text-muted-foreground tracking-tighter bg-muted/50 px-2 py-1 rounded-lg">
                            {formatSize(doc.dimensione_file)}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col text-xs font-black uppercase tracking-tight text-foreground/80">
                            {doc.profili?.nome} {doc.profili?.cognome}
                            <span className="text-[10px] font-medium text-muted-foreground lowercase">{doc.profili?.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          {doc.admin_data ? (
                            <div className="flex flex-col items-center">
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-orange-500/10 text-orange-600 border border-orange-500/20">
                                {doc.admin_data.nome} {doc.admin_data.cognome}
                              </span>
                              <span className="text-[9px] font-medium text-muted-foreground lowercase mt-1">{doc.admin_data.email}</span>
                            </div>
                          ) : (
                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                              {doc.deleted_by ? 'Admin' : 'System'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <Icon name="Clock" size={12} className="text-muted-foreground" />
                            <span className="text-xs font-bold text-muted-foreground whitespace-nowrap">
                              {formatFullDate(doc.deleted_at)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openModal('restore', doc)} disabled={actionId === doc.id} className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all active:scale-90">
                              <Icon name="RotateCcw" size={16} />
                            </button>
                            <button onClick={() => openModal('delete', doc)} disabled={actionId === doc.id} className="p-2 rounded-xl bg-red-600/10 text-red-600 hover:bg-red-600 hover:text-white transition-all active:scale-90">
                              <Icon name="Trash2" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-20 text-center text-muted-foreground opacity-30">
                        <Icon name="Search" size={40} className="mx-auto mb-2" />
                        <p className="text-[10px] font-black uppercase tracking-widest">{t('trash.no_files')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-red-500/[0.02] px-8 py-5 border-t border-border/50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                 <div className="p-2 bg-background rounded-lg border border-border"><Icon name="Info" size={14} className="text-red-500" /></div>
                 <p className="text-[9px] font-black uppercase tracking-[0.10em] text-muted-foreground max-w-md">
                   {autoDelete ? t('trash.info_auto_on') : t('trash.info_auto_off')}
                 </p>
              </div>
              <span className="text-[10px] font-black text-muted-foreground uppercase opacity-50">{t('trash.total')}: {filteredDocs.length}</span>
            </div>
          </div>
        </div>
      </main>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm animate-in fade-in" onClick={closeModal} />
          <div className="relative bg-card border border-border w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 animate-in zoom-in-95 flex flex-col">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${modalType === 'restore' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>
              <Icon name={modalType === 'restore' ? "RotateCcw" : "AlertTriangle"} size={32} />
            </div>
            <h3 className="text-xl font-black uppercase tracking-tighter mb-2 italic shrink-0">
              {modalType === 'restore' ? t('trash.modal_restore_title') : t('trash.modal_delete_title')}
            </h3>
            <div className="overflow-y-auto mb-8 pr-2">
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                {modalType === 'restore' ? (
                  t('trash.modal_restore_desc', { 
                    name: `"${selectedDoc?.nome_file}"`, 
                    owner: `${selectedDoc?.profili?.nome} ${selectedDoc?.profili?.cognome}` 
                  })
                ) : (
                  t('trash.modal_delete_desc', { 
                    name: `"${selectedDoc?.nome_file}"`, 
                    owner: `${selectedDoc?.profili?.nome} ${selectedDoc?.profili?.cognome}` 
                  })
                )}
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={closeModal} className="flex-1 px-6 py-4 rounded-2xl bg-muted/50 text-[11px] font-black uppercase tracking-widest hover:bg-muted transition-colors">
                {t('trash.modal_cancel')}
              </button>
              <button onClick={executeAction} disabled={actionId !== null} className={`flex-1 px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${modalType === 'restore' ? 'bg-emerald-600 shadow-emerald-600/20 hover:bg-emerald-700' : 'bg-red-600 shadow-red-600/20 hover:bg-red-700'}`}>
                {actionId ? <Icon name="Loader2" className="animate-spin" size={16} /> : t('trash.modal_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default CestinoAziendale;